import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { Alert, Badge, Input, Spinner } from "reactstrap";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { getPublicUrl } from "../../../utils/Utils";

const ACTION = new Set(["to_assign", "to_verify"]);
const WAIT = new Set(["awaiting_documents", "paid", "complements_requested"]);
const ASSIGNABLE = new Set(["to_assign", "to_verify", "paid", "awaiting_documents"]);

function patientName(row) {
  const p = row?.patient;
  if (!p || typeof p !== "object") return "—";
  return [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || p.email || "—";
}

function patientEmail(row) {
  const p = row?.patient;
  if (!p || typeof p !== "object") return "";
  return p.email || "";
}

function specialtyLabel(row) {
  const s = row?.specialty;
  if (s && typeof s === "object") return s.label || "—";
  return "—";
}

function offerLabel(row) {
  const o = row?.offer;
  if (o && typeof o === "object") return o.name || "—";
  return "—";
}

function caseRef(id) {
  const tail = String(id || "")
    .replace(/[^a-f0-9]/gi, "")
    .slice(-4)
    .toUpperCase();
  return tail ? `SO-${tail}` : "SO-—";
}

function dueDate(row) {
  const days =
    row?.offer?.slaDays != null
      ? Number(row.offer.slaDays)
      : row?.expert?.secondOpinionTurnaroundDays != null
        ? Number(row.expert.secondOpinionTurnaroundDays)
        : 5;
  const base = row?.paidAt || row?.createdAt;
  if (!base || !Number.isFinite(days)) return null;
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}

function fmtDue(d) {
  if (!d) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fmtWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isUrgent(row) {
  const due = dueDate(row);
  if (!due) return false;
  const ms = due.getTime() - Date.now();
  return ms < 48 * 3600 * 1000 && ASSIGNABLE.has(row.status);
}

function pill(status) {
  if (status === "draft") return { color: "#b45309", label: "Awaiting payment" };
  if (ACTION.has(status)) return { color: "#92400e", label: "Action required" };
  if (WAIT.has(status)) return { color: "#6b7280", label: "Waiting for patient" };
  return { color: "#2563eb", label: "In progress" };
}

function statusLine(row) {
  const due = fmtDue(dueDate(row));
  const expert = row?.expert?.displayName;
  if (row.status === "draft") {
    return `Started ${fmtWhen(row.createdAt)} · payment not completed`;
  }
  if (row.status === "to_assign") {
    return due ? `Ready to assign · due ${due}` : "Ready to assign";
  }
  if (row.status === "to_verify") return "Documents to verify";
  if (WAIT.has(row.status)) return "Waiting for patient documents";
  if (row.status === "in_review" || row.status === "assigned") {
    const bits = ["Analysis in progress"];
    if (expert) bits.push(expert);
    if (due) bits.push(`due ${due}`);
    return bits.join(" · ");
  }
  if (row.status === "complements_requested") return "Complements requested";
  return String(row.status || "").replace(/_/g, " ");
}

function matchesQuery(row, needle) {
  if (!needle) return true;
  const hay = [
    patientName(row),
    patientEmail(row),
    specialtyLabel(row),
    offerLabel(row),
    caseRef(row._id),
    String(row._id),
    row.status,
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

const SoCases = () => {
  const [tab, setTab] = useState("active");
  const [rows, setRows] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [remindBusy, setRemindBusy] = useState(null);

  const load = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        if (tab === "done") {
          const res = await http.get(`/api/v1/admin/second-opinion/cases?tab=done`, {
            signal,
            suppressInlineErrorSwal: true,
          });
          setRows(Array.isArray(res.data?.data) ? res.data.data : []);
          setFunnel([]);
        } else {
          const [activeRes, funnelRes] = await Promise.all([
            http.get(`/api/v1/admin/second-opinion/cases?tab=active`, {
              signal,
              suppressInlineErrorSwal: true,
            }),
            http.get(`/api/v1/admin/second-opinion/cases?tab=funnel`, {
              signal,
              suppressInlineErrorSwal: true,
            }),
          ]);
          setRows(Array.isArray(activeRes.data?.data) ? activeRes.data.data : []);
          setFunnel(Array.isArray(funnelRes.data?.data) ? funnelRes.data.data : []);
        }
      } catch (err) {
        if (isAbortError(err)) return;
        setError(err?.response?.data?.error || err?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    },
    [tab]
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const needle = q.trim().toLowerCase();
  const filtered = useMemo(() => rows.filter((r) => matchesQuery(r, needle)), [rows, needle]);
  const funnelFiltered = useMemo(
    () => funnel.filter((r) => matchesQuery(r, needle)),
    [funnel, needle]
  );

  const toAssign = filtered.filter((r) => r.status === "to_assign" || r.status === "to_verify");
  const inProgress = filtered.filter((r) => !toAssign.includes(r));
  const doneCount = tab === "done" ? filtered.length : null;
  const activeCount = tab === "active" ? filtered.length + funnelFiltered.length : null;

  const remind = async (row) => {
    setRemindBusy(String(row._id));
    setError("");
    const isDraft = row.status === "draft";
    try {
      await http.post(
        `/api/v1/admin/second-opinion/cases/${row._id}/remind`,
        {
          reason: isDraft
            ? "Please complete payment to start your second-opinion review."
            : "Please upload the missing documents for your second-opinion dossier.",
        },
        { suppressInlineErrorSwal: true }
      );
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Remind failed");
    } finally {
      setRemindBusy(null);
    }
  };

  const renderRow = (row, { showAssign, showRemind } = {}) => {
    const p = pill(row.status);
    const urgent = isUrgent(row);
    const id = String(row._id);
    const email = patientEmail(row);
    return (
      <div
        key={id}
        className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-3 border-bottom"
      >
        <div className="min-w-0">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <strong>
              {patientName(row)} · {specialtyLabel(row)}
            </strong>
            {urgent ? (
              <Badge style={{ background: "#fecaca", color: "#991b1b" }}>Urgent</Badge>
            ) : null}
          </div>
          <div className="small text-soft mt-1">
            {email ? <span className="me-2">{email}</span> : null}
            {row.status === "draft" ? <span className="me-2">Offer: {offerLabel(row)}</span> : null}
            <span className="me-2">{caseRef(id)}</span>
          </div>
          <div className="small text-soft mt-1 d-flex align-items-center gap-2">
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: p.color,
                display: "inline-block",
              }}
              aria-hidden
            />
            {statusLine(row)}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Link
            to={getPublicUrl(`second-opinion/cases/${id}`)}
            className="btn btn-icon btn-sm btn-outline-light"
            title="View"
          >
            <Icon name="eye" />
          </Link>
          {showAssign ? (
            <Link
              to={getPublicUrl(`second-opinion/experts?assign=${id}`)}
              className="btn btn-sm btn-primary"
            >
              Assign
            </Link>
          ) : null}
          {showRemind ? (
            <Button
              color="warning"
              size="sm"
              outline
              disabled={remindBusy === id}
              onClick={() => remind(row)}
            >
              <Icon name="mail" className="me-1" />
              Remind
            </Button>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <>
      <Head title="Second opinion cases" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Second opinion — cases</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {error ? (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        ) : null}

        <Block>
          <div className="d-flex gap-3 mb-3 border-bottom">
            <button
              type="button"
              className={`btn btn-link text-decoration-none px-0 pb-2 ${
                tab === "active" ? "border-bottom border-primary border-2 fw-bold" : "text-soft"
              }`}
              onClick={() => setTab("active")}
            >
              In progress {activeCount != null ? `(${activeCount})` : ""}
            </button>
            <button
              type="button"
              className={`btn btn-link text-decoration-none px-0 pb-2 ${
                tab === "done" ? "border-bottom border-primary border-2 fw-bold" : "text-soft"
              }`}
              onClick={() => setTab("done")}
            >
              Done {doneCount != null ? `(${doneCount})` : ""}
            </button>
          </div>

          <Input
            className="mb-3"
            placeholder="Search a case or patient"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <div className="d-flex flex-wrap gap-3 small text-soft mb-3">
            <span>
              <span
                className="d-inline-block rounded-circle me-1"
                style={{ width: 8, height: 8, background: "#b45309" }}
              />
              Awaiting payment
            </span>
            <span>
              <span
                className="d-inline-block rounded-circle me-1"
                style={{ width: 8, height: 8, background: "#92400e" }}
              />
              Action required
            </span>
            <span>
              <span
                className="d-inline-block rounded-circle me-1"
                style={{ width: 8, height: 8, background: "#2563eb" }}
              />
              In progress
            </span>
            <span>
              <span
                className="d-inline-block rounded-circle me-1"
                style={{ width: 8, height: 8, background: "#6b7280" }}
              />
              Waiting for patient
            </span>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner size="sm" />
            </div>
          ) : tab === "done" ? (
            <div className="card card-bordered">
              <div className="card-inner">
                {filtered.length === 0 ? (
                  <p className="text-soft mb-0">No completed cases.</p>
                ) : (
                  filtered.map((r) => renderRow(r, {}))
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="card card-bordered mb-3">
                <div className="card-inner">
                  <h6 className="title mb-2">Awaiting payment · {funnelFiltered.length}</h6>
                  <p className="small text-soft mb-2">
                    Patients who started a second-opinion request but stopped before paying. Use Remind
                    to send a resume link.
                  </p>
                  {funnelFiltered.length === 0 ? (
                    <p className="text-soft mb-0 small">No unpaid drafts.</p>
                  ) : (
                    funnelFiltered.map((r) => renderRow(r, { showRemind: true }))
                  )}
                </div>
              </div>
              <div className="card card-bordered mb-3">
                <div className="card-inner">
                  <h6 className="title mb-2">To assign · {toAssign.length}</h6>
                  {toAssign.length === 0 ? (
                    <p className="text-soft mb-0 small">No cases waiting for assignment.</p>
                  ) : (
                    toAssign.map((r) => renderRow(r, { showAssign: true }))
                  )}
                </div>
              </div>
              <div className="card card-bordered">
                <div className="card-inner">
                  <h6 className="title mb-2">In progress · {inProgress.length}</h6>
                  {inProgress.length === 0 ? (
                    <p className="text-soft mb-0 small">No cases in progress.</p>
                  ) : (
                    inProgress.map((r) =>
                      renderRow(r, { showRemind: WAIT.has(r.status) })
                    )
                  )}
                </div>
              </div>
            </>
          )}
        </Block>
      </Content>
    </>
  );
};

export default SoCases;
